import { ICampaignServer } from "model/campaigns";
import { BudgetRangeServer } from "model/campaigns/budgets";
import React from "react";
import {
  isCurrentCampaignBudget,
  useAllCampaignBudgets,
  useAllCampaignCurrentSpends,
} from "redux/campaignsBudgets/selectors";
import { getRunningCampaignsList } from "redux/campaignStatus/campaignStatusSelectors";
import { useSelector } from "redux/hooks";
import { CampaignPageNotificationType, NotificationReason } from "../types";
import { getNotificationId } from "./helper";

const generateNotifications = (campaigns: Array<CampaignWithDatedBudgets>) => {
  const notifications: Array<CampaignPageNotificationType> = [];

  campaigns.forEach((campaign) => {
    if (!campaign.datedBudgets?.length) return;

    const activeBudget = campaign.datedBudgets?.find(isCurrentCampaignBudget);

    if (!activeBudget) return;

    if ((campaign.currentSpend || 0) >= activeBudget.budget * 0.9) {
      notifications.push({
        id: getNotificationId({
          campaign,
          lineitem: activeBudget,
          reason: NotificationReason.campaignBudgetSpendOver90,
        }),
        campaignId: campaign?.id,
        campaignName: campaign?.name,
        reason: NotificationReason.campaignBudgetSpendOver90,
        budget: activeBudget,
      });
    }
  });

  return notifications;
};

export type CampaignWithDatedBudgets = ICampaignServer & {
  datedBudgets: BudgetRangeServer[];
  currentBudget?: BudgetRangeServer;
  currentSpend?: number;
};

export default function useCampaignBudgetNotifications() {
  const budgetsMap = useAllCampaignBudgets();
  const campaignSpends = useAllCampaignCurrentSpends();

  const campaignsList: CampaignWithDatedBudgets[] = useSelector(
    getRunningCampaignsList
  ).map((c) => {
    return {
      ...c,
      datedBudgets: budgetsMap[c.id] || [],
      currentBudget: budgetsMap[c.id]?.find(isCurrentCampaignBudget),
      currentSpend: campaignSpends[c.id] || 0,
    };
  });

  const syncNotifications: Array<CampaignPageNotificationType> = React.useMemo(
    () => generateNotifications(campaignsList),
    [campaignsList]
  );

  return syncNotifications;
}
